import {
  DeviceStreamProfileInput,
  StreamConfig,
  StreamData,
  StreamOption,
  StreamProfile,
} from "@onmo/graphql/graphql";
import i18next from "i18next";

export const AUTO_ID = "auto";
export const CUSTOM_ID = "custom";

export const getProfileLabel = (id?: string | null, label?: string | null) => {
  const key = `Config_${id}`;
  return id && i18next.exists(key) ? i18next.t(key) : label ?? "";
};

export type StreamConfigValue = {
  resolution: string;
  fps: number;
  bitrate: number;
};

export type PartialStreamConfig = Partial<StreamConfigValue>;

export const isCompleteConfig = (
  config: PartialStreamConfig | undefined,
): config is StreamConfigValue =>
  config?.resolution !== undefined &&
  config.fps !== undefined &&
  config.bitrate !== undefined;

export type DropdownOption = {
  value: string;
  label: string;
  locked: boolean;
};

export const sortProfiles = (
  profiles: readonly StreamProfile[],
): StreamProfile[] => [...profiles].sort((a, b) => a.order - b.order);

export const findProfile = (
  profiles: readonly StreamProfile[],
  id: string,
): StreamProfile | undefined => profiles.find((p) => p.id === id);

export type BitrateLimits = { min: number; max: number };
export const toBitrateLimits = (
  streamData: StreamData | null | undefined,
): BitrateLimits | undefined => {
  const bitrate = streamData?.limits?.bitrate;
  if (!bitrate) return undefined;
  const min = Number(bitrate.min);
  const max = Number(bitrate.max);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) {
    return undefined;
  }
  return { min, max };
};

export const formatBitrate = (kbps: number): string =>
  `${Math.round(kbps / 1000)} Mbps`;

export const formatResolution = (value: string): string => `${value}p`;

export const formatFps = (value: number | string): string => `${value} FPS`;

export const toStreamConfigValue = (
  config: StreamConfig,
): StreamConfigValue => ({
  resolution: config.resolution,
  fps: config.fps,
  bitrate: config.bitrate,
});

export const toDropdownOptions = (
  options: readonly StreamOption[] | undefined,
  format: (value: string) => string,
): DropdownOption[] =>
  (options ?? []).map((option) => ({
    value: option.value,
    label: format(option.value),
    locked: !option.enabled,
  }));

export type CustomField = "resolution" | "fps" | "bitrate";

export const buildCustomOptions = (
  profiles: readonly StreamProfile[],
): Record<CustomField, DropdownOption[]> => {
  const custom = findProfile(profiles, CUSTOM_ID)?.options;
  return {
    resolution: toDropdownOptions(
      custom?.supportedResolution,
      formatResolution,
    ),
    fps: toDropdownOptions(custom?.supportedFPS, formatFps),
    bitrate: toDropdownOptions(custom?.supportedBitrate, (value) =>
      formatBitrate(Number(value)),
    ),
  };
};

export const isSameConfig = (
  a: StreamConfigValue | undefined,
  b: StreamConfigValue | undefined,
): boolean =>
  a?.resolution === b?.resolution &&
  a?.fps === b?.fps &&
  a?.bitrate === b?.bitrate;

export const hasResolutionChanged = (
  applied: StreamConfigValue | undefined,
  next: StreamConfigValue | undefined,
): boolean => applied?.resolution !== next?.resolution;

export const toStreamProfileInput = (
  id: string,
  config?: StreamConfigValue,
): DeviceStreamProfileInput => {
  if (id !== CUSTOM_ID || !config) return { id };
  return {
    id,
    config: {
      resolution: config.resolution,
      fps: config.fps,
      bitrate: config.bitrate,
    },
  };
};

export const formatServerLocationName = (name: string): string =>
  name
    .split(":")
    .map((part) =>
      part
        .split("_")
        .filter(Boolean)
        .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
        .join(" "),
    )
    .filter(Boolean)
    .join(", ");
